import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select } from '@factorialco/f0-react'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import { EmployeesEmployeeSelectorInput } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useCreateTrainingClassMembership } from 'modules/trainings/hooks/TrainingClassMemberships/useCreateTrainingClassMembership'
import { useTrainingClasses } from 'modules/trainings/hooks/TrainingClasses/useTrainingClasses'
import { useCreateTrainingMemberships } from 'modules/trainings/hooks/TrainingMemberships/useCreateTrainingMemberships'

export const AddParticipantsModal = ({ employeeIds }: { employeeIds: number[] }) => {
  const [toggled, setToggled] = useState(false)
  const [classIds, setClassIds] = useState<number[]>([])
  const { id } = useParams()
  const [showTerminatedEmployees, setShowTerminatedEmployees] = useLocalStorage<boolean>(
    'trainings-participants-showTerminatedEmployees',
    true
  )

  const [selectorEmployees, setSelectionEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const { company } = useCurrentSession()
  const { trainingClasses } = useTrainingClasses({ trainingId: Number(id) })
  const { createTrainingClassMemberships, loading: loadingCreateTrainingClassMemberships } =
    useCreateTrainingClassMembership()
  const { createTrainingMemberships, loading: loadingCreateTrainingMemberships } =
    useCreateTrainingMemberships()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const onSubmit = async () => {
    if (toggled && classIds.length > 0) {
      const response = await createTrainingClassMemberships({
        trainingClassIds: classIds,
        employeeIds: selectorEmployees?.ids || [],
        selection: selectorEmployees,
        companyId: company.id,
      })

      const handled = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.error'
          ),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.success'
          ),
        })

        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
    } else {
      onCreateTrainingMemberships()
    }
  }
  const onCreateTrainingMemberships = async () => {
    const response = await createTrainingMemberships({
      employeeIds: selectorEmployees?.ids || [],
      selection: selectorEmployees,
      trainingId: Number(id),
    })

    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.error'
        ),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.success'
        ),
      })

      navigate(
        navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
      )
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={() =>
        navigate(
          navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
        )
      }
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.title'
      )}
      primaryAction={{
        label: i18n.t(
          'trainings.trainings_revamp.training_detail.employees_table.assign_modal.assign'
        ),
        onClick: () => onSubmit(),
        loading: loadingCreateTrainingClassMemberships || loadingCreateTrainingMemberships,
        disabled: loadingCreateTrainingClassMemberships || loadingCreateTrainingMemberships,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: () =>
          navigate(
            navigation.trainings.list.details_revamp.view.participants.fullPath({ id: Number(id) })
          ),
      }}
    >
      <Box gap="s32">
        <EmployeeSelectorV2
          multiple
          forcedFilters={{
            excludeIds: employeeIds,
          }}
          onChange={(state) => setSelectionEmployees(state)}
          options={{
            asList: true,
          }}
        />
        <Switch
          checked={toggled}
          onCheckedChange={(checked: boolean) => setToggled(checked)}
          title={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.toggle'
          )}
        />
        {toggled && (
          <Box height="fit-content" gap="s12">
            <F0Select
              multiple
              disableSelectAll
              size="md"
              label={i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.assign_modal.classes'
              )}
              searchBoxPlaceholder={i18n.t('common.search')}
              options={trainingClasses.map((trainingClass) => ({
                label: trainingClass.name,
                value: trainingClass.id,
              }))}
              onChange={(selection: string[]) => {
                const result = selection.map((item) => Number(item))
                setClassIds(result)
              }}
              placeholder={i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.assign_modal.placeholder'
              )}
              showSearchBox
              isDisabled={trainingClasses.length === 0}
            />

            {trainingClasses.length === 0 && (
              <F0Alert
                title={i18n.t(
                  'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.callout_title'
                )}
                description={i18n.t(
                  'trainings.trainings_revamp.training_detail.employees_table.add_participants_modal.callout_description'
                )}
                variant="info"
              />
            )}
          </Box>
        )}
      </Box>
    </F0Dialog>
  )
}
